/**
 * Phase 15.1 — Optimisation section.
 *
 * Toggle + GA-config panel for the unified Campaign Builder. Dispatches
 * the "optimisation" lane to POST /api/optimisation-campaigns. Form
 * data is held by the wizard's react-hook-form state; this component
 * is a controlled leaf.
 *
 * Decision-variable budget: the GA searches the 8 variables shipped
 * in the backend registry (``DECISION_VARIABLES``). Operators do not
 * pick subsets at this stage — that's a Phase 15.x knob.
 */
import { Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const OPTIMISATION_DEFAULTS = Object.freeze({
    enabled: false,
    population_size: 32,
    generations: 15,
    mc_seeds_per_individual: 20,
    max_workers: 4,
    seed: 42,
});

export const OPTIMISATION_BOUNDS = Object.freeze({
    population_size: { min: 8, max: 128 },
    generations: { min: 3, max: 50 },
    mc_seeds_per_individual: { min: 2, max: 100 },
    max_workers: { min: 1, max: 16 },
    seed: { min: 0, max: 2147483647 },
});

export default function OptimisationSection({
    enabled = false,
    onToggleEnabled,
    value,
    onChange,
}) {
    const v = value || OPTIMISATION_DEFAULTS;
    const totalRuns =
        Number(v.population_size || 0)
        * Number(v.generations || 0)
        * Number(v.mc_seeds_per_individual || 0);
    const overCap = totalRuns > 250000;

    const update = (patch) => {
        onChange?.({ ...(value || OPTIMISATION_DEFAULTS), ...patch });
    };

    const numField = (key, label, hint) => {
        const b = OPTIMISATION_BOUNDS[key];
        return (
            <div className="space-y-1">
                <Label
                    className="block text-xs font-medium uppercase tracking-wide text-ux-text-3"
                    htmlFor={`optimisation-${key}`}
                >
                    {label}
                </Label>
                <Input
                    id={`optimisation-${key}`}
                    type="number"
                    min={b.min}
                    max={b.max}
                    value={v[key] ?? ""}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const parsed = raw === "" ? "" : Number(raw);
                        update({ [key]: parsed });
                    }}
                    data-testid={`optimisation-${key}-input`}
                />
                {hint ? (
                    <p className="text-[11px] text-ux-text-3">{hint}</p>
                ) : null}
            </div>
        );
    };

    return (
        <section
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
            data-testid="section-optimisation"
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-start gap-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid="toggle-optimisation"
                    aria-label="Enable optimisation"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Sparkles className="size-4 text-ux-text-3" />
                        <span className="text-sm font-medium text-ux-text-1">
                            Optimisation (GA)
                        </span>
                    </div>
                    <p className="text-xs text-ux-text-3">
                        Search guidance gains + launch geometry with a Genetic Algorithm
                        to maximise Pk. Local concurrent workers fan out per-individual
                        Monte Carlo evaluations.
                    </p>
                </div>
            </div>

            {enabled && (
                <div className="mt-4 space-y-3" data-testid="optimisation-details">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {numField(
                            "population_size",
                            "Population size",
                            "8 – 128 individuals per generation",
                        )}
                        {numField(
                            "generations",
                            "Generations",
                            "3 – 50 GA iterations",
                        )}
                        {numField(
                            "mc_seeds_per_individual",
                            "MC seeds / individual",
                            "2 – 100 Monte Carlo seeds per individual",
                        )}
                        {numField(
                            "max_workers",
                            "Max concurrent workers",
                            "1 – 16 (capped at host CPU count)",
                        )}
                        {numField(
                            "seed",
                            "GA RNG seed",
                            "Reproducibility — same seed, same result",
                        )}
                    </div>
                    <div
                        className={[
                            "rounded-md border p-2 text-xs",
                            overCap
                                ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                                : "border-ux-border-1 bg-ux-surface-2/40 text-ux-text-3",
                        ].join(" ")}
                        data-testid="optimisation-total-runs-readout"
                    >
                        Estimated total simulation runs:{" "}
                        <strong className="text-ux-text-1">
                            {Number(totalRuns).toLocaleString()}
                        </strong>{" "}
                        (population × generations × MC seeds).{" "}
                        {overCap
                            ? "This is unusually large — consider reducing one of the three."
                            : "Workers will run concurrently up to the host CPU limit."}
                    </div>
                </div>
            )}
        </section>
    );
}
